import { useQueryClient } from '@tanstack/react-query';

import { useSecretMutation } from '@/hooks/use-secret-mutation';

import { enrollmentService } from '@/services/enrollment-service';
import type {
  CreateEnrollmentSecretPayload,
  CreateEnrollmentSecretResult,
} from '@/types/core/enrollment';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { enrollmentMutationKeys, enrollmentQueryKeys } from './query-keys';

/**
 * Mint a shared enrollment secret. Returns the secret once — it is never
 * retrievable again. Waits for the read model before resolving so the new
 * credential is visible in the listing on success.
 */
export function useCreateEnrollmentSecret(tenantId?: UUID) {
  const qc = useQueryClient();

  return useSecretMutation<
    CreateEnrollmentSecretResult,
    CreateEnrollmentSecretPayload
  >({
    mutationKey: enrollmentMutationKeys.createSecret(tenantId),
    mutationFn: async (payload) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const result = await enrollmentService.createSecret(tenantId, payload);

      await pollForResource(async () => {
        const credentials = await enrollmentService.listCredentials(tenantId);
        return (
          credentials.find((c) => c.credentialId === result.credentialId) ??
          null
        );
      });

      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: enrollmentQueryKeys.credentialsByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
